import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ListBulletIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import aiSparkIcon from '../../../../../assets/svg/icons/Ai-Spark-Starlight--Streamline-Flex.svg';
import calendarIcon from '../../../../../assets/svg/icons/Calendar-Smiley-Happy-Face--Streamline-Flex.svg';
import newFolderIcon from '../../../../../assets/svg/icons/New-Folder--Streamline-Flex.svg';
import addCircleIcon from '../../../../../assets/svg/icons/Add-Circle--Streamline-Flex.svg';
import uploadFileIcon from '../../../../../assets/svg/icons/Upload-File--Streamline-Flex.svg';
import folderIcon from '../../../../../assets/svg/icons/Open-Folder--Streamline-Flex.svg';
import refreshIcon from '../../../../../assets/svg/icons/Line-Arrow--Streamline-Flex.svg';
import graphDotIcon from '../../../../../assets/svg/icons/Graph-Dot--Streamline-Flex.svg';
import roadmapIcon from '../../../../../assets/svg/icons/Timeline-Roadmap-Horizontal--Streamline-Flex.svg';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  isCopanySubAdmin
} from '../../../../../utils/helper';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import AddDocumentFolderModal from '../components/AddDocumentFolderModal';
import ComingSoonModal from '../components/ComingSoonModal';
import { EmptyState } from '../../../../../component/common/StandardUI';
import ChecklistCheckbox from '../components/ChecklistCheckbox';
import targetIcon from '../../../../../assets/svg/icons/Target--Streamline-Flex.svg';

const formatKeyDateLabel = (keyDate) => {
  if (!keyDate) return '';
  const monthYear = moment()
    .month(keyDate.month - 1)
    .year(keyDate.year)
    .format('MMMM YYYY');
  if (!keyDate.day) return monthYear;
  return moment(new Date(keyDate.year, keyDate.month - 1, keyDate.day)).format('D MMMM YYYY');
};

const checklistCompletion = (checklist) => {
  if (!Array.isArray(checklist) || checklist.length === 0) return 0;
  const completed = checklist.filter((item) => item.isCompleted).length;
  return Math.round((completed / checklist.length) * 100);
};

// h-full + flex-1 body lets a card fill its grid row, so cards sitting in the
// same row line up and their footers sit on a common baseline.
const SectionCard = ({ title, description, actions, children, footer, className = '' }) => (
  <div
    className={`h-full flex flex-col rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-5 sm:p-6 ${className}`}
  >
    <div className="flex items-start justify-between gap-3">
      <h3 className="text-base font-medium text-gray-700 dark:text-gray-200">{title}</h3>
      {actions?.length > 0 && (
        <div className="flex items-center gap-2 shrink-0">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              aria-label={action.label}
              className="text-gray-400 hover:text-shoorah-primary hover:opacity-70 transition-all"
            >
              {action.icon}
            </button>
          ))}
        </div>
      )}
    </div>
    <p className="text-sm text-gray-800 mt-1 mb-4">{description}</p>
    <div className="flex-1">{children}</div>
    {footer && <div className="mt-4">{footer}</div>}
  </div>
);

const ManageLink = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-sm font-medium text-shoorah-btn dark:text-shoorah-btnDark hover:underline"
  >
    Manage
  </button>
);

const EmptyRow = ({ text }) => <EmptyState title={text} />;

const NavRow = ({ icon, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center gap-4 rounded-input border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder p-4 text-left hover:border-shoorah-btn dark:hover:border-shoorah-btnDark transition-colors"
  >
    {icon}
    <div className="flex-1 min-w-0">
      <p className="font-medium text-gray-700 dark:text-gray-200">{title}</p>
      <p className="text-xs text-gray-400 mt-1">{description}</p>
    </div>
    <ChevronRightIcon className="w-5 h-5 text-gray-300 shrink-0" />
  </button>
);

const PeoplePlanDashboard = () => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isSubAdmin = isCopanySubAdmin();
  const basePath = `/${slug}/people-plan`;
  const pages = [{ name: 'People plan', href: basePath, current: true }];

  const [folders, setFolders] = useState([]);
  const [upcomingKeyDates, setUpcomingKeyDates] = useState([]);
  const [goals, setGoals] = useState([]);
  const [milestone, setMilestone] = useState(null);
  const [hasRoiReport, setHasRoiReport] = useState(false);
  const [hasRoadMap, setHasRoadMap] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [isRoiComingSoonOpen, setIsRoiComingSoonOpen] = useState(false);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await CompanyApi.getAllDocumentFolders(1, 5);
      setFolders(response?.data?.meta?.code === 1 ? response?.data?.data || [] : []);
    } catch (error) {
      errorToast(error.message || 'Failed to load document folders');
    }
  }, []);

  const fetchKeyDates = useCallback(async () => {
    try {
      // Key dates on the dashboard are date-driven: the next two events from
      // today, which the API resolves across month boundaries.
      const response = await CompanyApi.getUpcomingKeyDates(2);
      setUpcomingKeyDates(response?.data?.meta?.code === 1 ? response?.data?.data || [] : []);
    } catch (error) {
      setUpcomingKeyDates([]);
    }
  }, []);

  const fetchGoals = useCallback(async () => {
    try {
      const response = await CompanyApi.getAllGoals(1, 3);
      setGoals(response?.data?.meta?.code === 1 ? response?.data?.data || [] : []);
    } catch (error) {
      errorToast(error.message || 'Failed to load goals');
    }
  }, []);

  const fetchMilestone = useCallback(async () => {
    try {
      // Nearest deadline first, so the dashboard surfaces the most urgent milestone
      const response = await CompanyApi.getAllMilestones(1, 1, {
        sortBy: 'endDate',
        sortOrder: 'asc'
      });
      const list = response?.data?.meta?.code === 1 ? response?.data?.data || [] : [];
      setMilestone(list[0] || null);
    } catch (error) {
      errorToast(error.message || 'Failed to load milestones');
    }
  }, []);

  const fetchRoiReport = useCallback(async () => {
    try {
      const response = await CompanyApi.fetchRoiReport();
      setHasRoiReport(!!(response?.data?.meta?.code === 1 && response?.data?.data));
    } catch (error) {
      setHasRoiReport(false);
    }
  }, []);

  const fetchRoadMaps = useCallback(async () => {
    try {
      const response = await CompanyApi.getAllRoadMaps(1, 1);
      setHasRoadMap(!!(response?.data?.meta?.code === 1 && response?.data?.data?.length));
    } catch (error) {
      setHasRoadMap(false);
    }
  }, []);

  // Suggestions are cached server-side; the first load of the day pays for the
  // AI call, so this is kept out of the main fetch batch with its own spinner.
  const fetchSuggestions = useCallback(async () => {
    try {
      setSuggestionsLoading(true);
      const response = await CompanyApi.getPeoplePlanSuggestions('overview');
      setSuggestions(response?.data?.meta?.code === 1 ? response?.data?.data || [] : []);
    } catch (error) {
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const handleRefreshSuggestions = async () => {
    try {
      setSuggestionsLoading(true);
      const response = await CompanyApi.refreshPeoplePlanSuggestions('overview');
      if (response?.data?.meta?.code === 1) {
        setSuggestions(response?.data?.data || []);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to refresh suggestions');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to refresh suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
    fetchKeyDates();
    fetchGoals();
    fetchMilestone();
    fetchRoiReport();
    fetchRoadMaps();
    fetchSuggestions();
  }, [
    fetchFolders,
    fetchKeyDates,
    fetchGoals,
    fetchMilestone,
    fetchRoiReport,
    fetchRoadMaps,
    fetchSuggestions
  ]);

  const handleCreateFolder = async (name) => {
    try {
      setCreatingFolder(true);
      const response = await CompanyApi.createDocumentFolder({ name });
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Folder created successfully');
        setIsFolderModalOpen(false);
        fetchFolders();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to create folder');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleToggleMilestoneItem = async (checklistItemId) => {
    try {
      setTogglingId(checklistItemId);
      const response = await CompanyApi.toggleMilestoneChecklistItem(
        milestone._id,
        checklistItemId
      );
      if (response?.data?.meta?.code === 1) {
        fetchMilestone();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to update checklist item');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to update checklist item');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>People plan</title>
      </Helmet>
      <Topbar title="People plan" infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        {/* Key dates */}
        <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-shoorah-borderGray dark:divide-shoorah-darkBorder">
            <div className="flex items-center gap-3 p-5 sm:p-6">
              <div className="w-12 h-12 rounded-full border border-shoorah-borderGray dark:border-shoorah-darkBorder bg-shoorah-normanGray dark:bg-shoorah-darkBgColor flex items-center justify-center shrink-0">
                <img src={calendarIcon} alt="" className="w-6 h-6 dark:invert" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-gray-700 dark:text-gray-200">Key dates</p>
                <p className="text-xs text-gray-800">Upcoming dates you should be aware of.</p>
              </div>
            </div>

            {[0, 1].map((index) => {
              const item = upcomingKeyDates[index];
              return (
                <div key={index} className="flex flex-col justify-center p-5 sm:p-6">
                  {item ? (
                    <>
                      <p className="text-sm font-medium text-shoorah-btn dark:text-gray-200 line-clamp-2">
                        {item.title}
                      </p>
                      <p className="text-xs text-shoorah-btn dark:text-gray-300 mt-1">
                        {formatKeyDateLabel(item)}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">No upcoming key dates.</p>
                  )}
                </div>
              );
            })}

            <div className="flex items-center justify-center p-5 sm:p-6">
              <button
                type="button"
                onClick={() => navigate(`${basePath}/key-dates`)}
                className="text-sm font-medium text-gray-700 dark:text-gray-200 underline underline-offset-2 hover:text-shoorah-primary transition-colors"
              >
                View all
              </button>
            </div>
          </div>
        </div>

        {/* Two-column grid: row 1 = Document hub / Goals overview,
            row 2 = Milestones / (ROI + Roadmap stacked). Grid rows stretch, so
            paired cards always match height instead of depending on content. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Document hub */}
          <SectionCard
            className="lg:col-start-1 lg:row-start-1"
            title="Document hub"
            description="Store and organise all your internal HR and wellbeing documents in one central place."
            actions={
              isSubAdmin
                ? []
                : [
                  //
                  {
                    label: 'Go to Document Hub',
                    icon: <img src={folderIcon} alt="" className="w-5 h-5 dark:invert" />,
                    onClick: () => navigate(`${basePath}/document-hub`)
                  },
                  {
                    label: 'Create folder',
                    icon: <img src={addCircleIcon} alt="" className="w-5 h-5 dark:invert" />,
                    onClick: () => setIsFolderModalOpen(true)
                  }
                ]
            }
          >
            <div className="grid grid-cols-2 gap-3">
              {folders.slice(0, 5).map((folder) => (
                <button
                  key={folder._id}
                  type="button"
                  onClick={() => navigate(`${basePath}/document-hub/${folder._id}`)}
                  className="flex items-center gap-3 rounded-input border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder p-4 text-left hover:border-shoorah-btn dark:hover:border-shoorah-btnDark transition-colors"
                >
                  <img src={newFolderIcon} alt="" className="w-6 h-6 shrink-0 dark:invert" />
                  <span className="text-sm text-gray-700 dark:text-gray-200 line-clamp-2">
                    {folder.name}
                  </span>
                </button>
              ))}
              {!isSubAdmin && folders.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsFolderModalOpen(true)}
                  className="flex items-center gap-3 rounded-input border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder p-4 text-left hover:border-shoorah-btn dark:hover:border-shoorah-btnDark transition-colors"
                >
                  <img src={addCircleIcon} alt="" className="w-6 h-6 shrink-0 dark:invert" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Create a new folder
                  </span>
                </button>
              )}
            </div>
            {folders.length === 0 && (
              <div className="mt-3">
                <EmptyRow text="No folders yet." />
              </div>
            )}
          </SectionCard>

          {/* Milestones & checklists */}
          <SectionCard
            className="lg:col-start-1 lg:row-start-2"
            title="Milestones & checklists"
            description="Break down your goals into actionable steps and stay on top of key initiatives."
            actions={
              isSubAdmin
                ? []
                : [
                  {
                    label: 'New milestone',
                    icon: <img src={addCircleIcon} alt="" className="w-5 h-5 dark:invert" />,
                    onClick: () => navigate(`${basePath}/milestones/add`)
                  }
                ]
            }
            footer={<ManageLink onClick={() => navigate(`${basePath}/milestones`)} />}
          >
            {milestone ? (
              <button
                type="button"
                onClick={() => navigate(`${basePath}/milestones/view/${milestone._id}`)}
                className="w-full text-left rounded-input border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder p-4 hover:border-shoorah-btn dark:hover:border-shoorah-btnDark transition-colors"
              >
                <div className="flex items-start gap-3 mb-3">
                  <ListBulletIcon className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-700 dark:text-gray-200">
                      {milestone.title}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Due date:{' '}
                      {milestone.endDate ? moment(milestone.endDate).format('Do MMMM YYYY') : '—'}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {(milestone.checklist || []).map((item) => (
                    <div
                      key={item._id}
                      className="flex items-center justify-between gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (togglingId !== item._id) handleToggleMilestoneItem(item._id);
                      }}
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {item.text}
                      </span>
                      <ChecklistCheckbox
                        size="sm"
                        checked={!!item.isCompleted}
                        disabled={togglingId === item._id}
                        onToggle={() => handleToggleMilestoneItem(item._id)}
                        label={`Mark "${item.text}" complete`}
                      />
                    </div>
                  ))}
                </div>
              </button>
            ) : (
              <EmptyRow text="No milestones yet." />
            )}
          </SectionCard>

          {/* Goals overview */}
          <SectionCard
            className="lg:col-start-2 lg:row-start-1"
            title="Goals overview"
            description="Set and track meaningful company wellbeing goals to drive impact and accountability."
            actions={
              isSubAdmin
                ? []
                : [
                  {
                    label: 'New goal',
                    icon: <img src={addCircleIcon} alt="" className="w-5 h-5 dark:invert" />,
                    onClick: () => navigate(`${basePath}/goals/add`)
                  }
                ]
            }
            footer={<ManageLink onClick={() => navigate(`${basePath}/goals`)} />}
          >
            {goals.length === 0 ? (
              <EmptyRow text="No goals yet." />
            ) : (
              <div className="space-y-3">
                {goals.map((goal) => {
                  const percentage = checklistCompletion(goal.checklist);
                  return (
                    <button
                      key={goal._id}
                      type="button"
                      onClick={() => navigate(`${basePath}/goals/view/${goal._id}`)}
                      className="w-full text-left rounded-input border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder p-4 hover:border-shoorah-btn dark:hover:border-shoorah-btnDark transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <img
                          src={targetIcon}
                          alt=""
                          className="w-5 h-5 shrink-0 mt-0.5 dark:invert"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-700 dark:text-gray-200">
                            {goal.title}
                          </p>
                          <p className="text-xs text-gray-800 text-right mt-3">
                            {percentage}% until goal reached
                          </p>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-shoorah-progressGreenLight dark:bg-shoorah-progressGreen/30 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-shoorah-progressGreen"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </SectionCard>


          <div className="lg:col-start-2 lg:row-start-2 flex flex-col gap-6">


            <SectionCard
              className="flex-1"
              title="Roadmap"
              description="Develop a strategic roadmap outlining key internal goals and milestones."
            >
              <NavRow
                icon={<img src={roadmapIcon} alt="" className="w-6 h-6 shrink-0 dark:invert" />}
                title="Generate & manage"
                description={
                  hasRoadMap
                    ? 'View and manage your company roadmaps.'
                    : 'Develop a strategic roadmap outlining key internal goals and milestones.'
                }
                onClick={() => navigate(`${basePath}/roadmaps`)}
              />
            </SectionCard>
            <SectionCard
              className="flex-1"
              title="ROI report"
              description="Understand the business impact of your wellbeing strategy with data-driven insights."
            >
              <NavRow
                icon={<img src={graphDotIcon} alt="" className="w-6 h-6 shrink-0 dark:invert" />}
                title="Generate & manage"
                description={
                  hasRoiReport
                    ? 'Your ROI report, featuring key data insights, cost savings, and strategic recommendations.'
                    : 'Generate your ROI report to unlock key data insights, cost savings and strategic recommendations.'
                }
                // ROI report is not being surfaced yet — the page and route are
                // untouched, this just stands in front of the entry point.
                onClick={() => setIsRoiComingSoonOpen(true)}
              />
            </SectionCard>


          </div>
        </div>

        {/* Suggestions — AI recommendations across goals, milestones,
            road maps and this month's key dates. Full width, end of page. */}
        <div className="mt-6">
          <SectionCard
            title="Suggestions"
            description="Get smart, timely recommendations to strengthen your people strategy and workplace culture."
            actions={
              isSubAdmin
                ? []
                : [
                  {
                    label: 'Refresh suggestions',
                    icon: <img src={refreshIcon} alt="" className="w-5 h-5 dark:invert" />,
                    onClick: handleRefreshSuggestions
                  }
                ]
            }
          >
            {suggestionsLoading ? (
              <p className="text-sm text-gray-400">Analysing your People Plan…</p>
            ) : suggestions.length === 0 ? (
              <EmptyRow text="No suggestions yet. Add goals, milestones or road maps to get recommendations." />
            ) : (
              <div className="space-y-4">
                {suggestions.map((item, index) => (
                  <div
                    key={item._id}
                    className="rounded-input bg-shoorah-normanGray dark:bg-shoorah-darkBgColor p-4"
                  >
                    <div className="flex items-center gap-3">
                      <img src={aiSparkIcon} alt="" className="w-5 h-5 shrink-0 dark:invert" />
                      <p className="font-medium text-gray-700 dark:text-gray-200">
                        {index + 1}. {item.title}
                      </p>
                    </div>
                    {item.insight && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">
                        <span className="font-medium text-gray-700 dark:text-gray-200">
                          Insight:
                        </span>{' '}
                        {item.insight}
                      </p>
                    )}
                    {item.suggestion && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        <span className="font-medium text-gray-700 dark:text-gray-200">
                          Suggestion:
                        </span>{' '}
                        {item.suggestion}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      <AddDocumentFolderModal
        open={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        onSubmit={handleCreateFolder}
        submitting={creatingFolder}
      />

      <ComingSoonModal
        open={isRoiComingSoonOpen}
        title="ROI report is coming soon"
        description="We're putting the finishing touches on your ROI report. You'll be able to generate and manage it here shortly."
        onClose={() => setIsRoiComingSoonOpen(false)}
      />
    </div>
  );
};

export default PeoplePlanDashboard;
